import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const newsletterSchema = z.object({
  variant: z.enum(['inline', 'centered', 'split']).default('inline'),
  eyebrow: z.string().max(120).optional(),
  h2: z.string().min(1).max(200),
  subtitle: z.string().max(400).optional(),
  form_key: z.string().min(1).max(80),
  cta_label: z.string().min(1).max(60).default('Suscribirme'),
  placeholder: z.string().max(120).default('tu@email.com'),
  legal_text: z.string().max(300).optional(),
  background: z.enum(['white', 'soft', 'dark']).default('soft'),
});

export type NewsletterData = z.infer<typeof newsletterSchema>;

export function NewsletterRenderer({
  variant = 'inline', eyebrow, h2, subtitle, form_key, cta_label, placeholder, legal_text, background = 'soft',
}: NewsletterData) {
  return (
    <section className={`v5-newsletter v5-newsletter-${variant} bg-${background}`}>
      <div className="container">
        <div className="v5-newsletter-grid">
          <div className="v5-newsletter-text">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            <h2 className="v5-h2-sm">{h2}</h2>
            {subtitle ? <p className="v5-lead-sm">{subtitle}</p> : null}
          </div>
          <form className="v5-newsletter-form" data-form-key={form_key} method="post" action={`/api/v1/public/forms/${form_key}/submit`}>
            <input
              type="email"
              name="email"
              required
              placeholder={placeholder}
              className="v5-newsletter-input"
              aria-label="Email"
            />
            <input type="text" name="_hp" tabIndex={-1} autoComplete="off" className="v5-newsletter-hp" />
            <button type="submit" className="v5-btn-primary">{cta_label}</button>
            {legal_text ? <small className="v5-newsletter-legal">{legal_text}</small> : null}
          </form>
        </div>
      </div>
    </section>
  );
}

export const newsletterBlock: BlockDefinition<NewsletterData> = {
  meta: {
    type: 'newsletter',
    label: 'Suscripción a newsletter',
    description: 'Formulario inline de captura de email. Envía a una Form registrada en el admin.',
    category: 'form',
    icon: 'mail',
  },
  schema: newsletterSchema,
  defaults: {
    variant: 'inline',
    eyebrow: 'NEWSLETTER',
    h2: 'Mantente al día',
    subtitle: 'Insights mensuales sobre M&A, valoraciones y mercado.',
    form_key: 'newsletter',
    cta_label: 'Suscribirme',
    placeholder: 'tu@email.com',
    legal_text: 'Sin spam. Date de baja cuando quieras.',
    background: 'soft',
  },
  fields: [
    {
      name: 'variant',
      label: 'Variante',
      type: 'select',
      options: [
        { label: 'Inline', value: 'inline' },
        { label: 'Centered', value: 'centered' },
        { label: 'Split (2 columnas)', value: 'split' },
      ],
    },
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h2', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 400 },
    { name: 'form_key', label: 'Clave del Form (form_key)', type: 'text', required: true, max: 80, helperText: 'Crea un Form en /forms con esta key.' },
    { name: 'cta_label', label: 'Texto botón', type: 'text', max: 60 },
    { name: 'placeholder', label: 'Placeholder email', type: 'text', max: 120 },
    { name: 'legal_text', label: 'Texto legal pequeño', type: 'text', max: 300 },
    {
      name: 'background',
      label: 'Fondo',
      type: 'select',
      options: [
        { label: 'Blanco', value: 'white' },
        { label: 'Suave', value: 'soft' },
        { label: 'Oscuro', value: 'dark' },
      ],
    },
  ],
  Renderer: NewsletterRenderer,
};
